// src/components/superadmin/DatabaseStats.tsx
"use client";

import { useEffect, useState } from "react";
import { api } from "../../services/Axios";
import {
  Database,
  HardDrive,
  Layers,
  FileText,
  Archive,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  Server,
} from "lucide-react";

// Función para formatear bytes a formato legible
const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
};

const DatabaseStats = () => {
  const [dbStats, setDbStats] = useState<null | {
    mainDB: {
      storageSize: number;
      dataSize: number;
      indexSize: number;
      collections: number;
      objects: number;
    };
    salesDB: {
      storageSize: number;
      dataSize: number;
      indexSize: number;
      collections: number;
      objects: number;
    };
  }>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const res = await api.get("/api/admin/db/storage-stats");
        setDbStats(res.data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching DB stats", err);
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
        <div className="flex flex-col justify-center items-center py-20">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mb-4"></div>
          <p className="text-gray-500 font-medium">Cargando estadísticas de base de datos...</p>
        </div>
      </div>
    );
  }

  if (!dbStats) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
        <div className="flex flex-col justify-center items-center py-20">
          <AlertTriangle className="h-16 w-16 text-red-400 mb-4" />
          <p className="text-gray-700 font-medium">Error al cargar las estadísticas</p>
        </div>
      </div>
    );
  }

  const totalUsedBytes =
    dbStats.mainDB.storageSize +
    dbStats.mainDB.indexSize +
    dbStats.salesDB.storageSize +
    dbStats.salesDB.indexSize;

  const totalUsedMB = totalUsedBytes / (1024 * 1024);
  const limitMB = 1024;
  const percentage = (totalUsedMB / limitMB) * 100;
  const remainingMB = limitMB - totalUsedMB;

  const getStatusColor = () => {
    if (percentage > 90) return "red";
    if (percentage > 70) return "yellow";
    return "green";
  };

  const getStatusIcon = () => {
    if (percentage > 90) return <AlertTriangle className="h-5 w-5" />;
    return <CheckCircle className="h-5 w-5" />;
  };

  const getStatusText = () => {
    if (percentage > 90) return "Crítico - Considerar limpieza";
    if (percentage > 70) return "Advertencia - Revisar pronto";
    return "Saludable";
  };

  const statusColor = getStatusColor();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg">
            <Database className="h-6 w-6 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Estadísticas de Almacenamiento</h1>
        </div>
        <p className="text-gray-600 ml-14">Monitorea el uso de las bases de datos del sistema</p>
      </div>

      {/* Summary Card */}
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-xl">
              <HardDrive className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Uso Total de Almacenamiento</h2>
              <p className="text-sm text-gray-500">Límite: {limitMB} MB</p>
            </div>
          </div>
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
            statusColor === "red" ? "bg-red-100 text-red-700" :
            statusColor === "yellow" ? "bg-yellow-100 text-yellow-700" :
            "bg-green-100 text-green-700"
          }`}>
            {getStatusIcon()}
            <span className="font-semibold text-sm">{getStatusText()}</span>
          </div>
        </div>

        <div className="space-y-3 mb-6">
          <div className="flex justify-between items-center">
            <span className="text-2xl font-bold text-gray-900">{totalUsedMB.toFixed(2)} MB</span>
            <span className="text-lg text-gray-500">de {limitMB} MB</span>
          </div>
          
          <div className="relative w-full h-6 bg-gray-200 rounded-full overflow-hidden shadow-inner">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                statusColor === "red" ? "bg-gradient-to-r from-red-500 to-red-600" :
                statusColor === "yellow" ? "bg-gradient-to-r from-yellow-500 to-yellow-600" :
                "bg-gradient-to-r from-green-500 to-green-600"
              }`}
              style={{ width: `${Math.min(percentage, 100)}%` }}
            >
              <div className="h-full w-full bg-white/20 animate-pulse"></div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-xs font-bold text-gray-700">{percentage.toFixed(1)}%</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 pt-2">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <span className="text-sm text-gray-600">Usado: {totalUsedMB.toFixed(2)} MB</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
              <span className="text-sm text-gray-600">Disponible: {remainingMB.toFixed(2)} MB</span>
            </div>
          </div>
        </div>

        {percentage > 70 && (
          <div className={`p-4 rounded-xl border-2 ${
            percentage > 90 ? "bg-red-50 border-red-200" : "bg-yellow-50 border-yellow-200"
          }`}>
            <div className="flex items-start gap-3">
              <AlertTriangle className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                percentage > 90 ? "text-red-600" : "text-yellow-600"
              }`} />
              <div>
                <p className={`font-semibold mb-1 ${
                  percentage > 90 ? "text-red-900" : "text-yellow-900"
                }`}>
                  {percentage > 90 ? "Acción Requerida" : "Advertencia"}
                </p>
                <p className={`text-sm ${
                  percentage > 90 ? "text-red-700" : "text-yellow-700"
                }`}>
                  El almacenamiento está llegando al límite. Considera eliminar datos antiguos o actualizar el plan.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Database Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Main Database */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow duration-300">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-md">
              <Server className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Base de Datos Principal</h2>
              <p className="text-sm text-gray-500">JadFost - Datos Principales</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <HardDrive className="h-5 w-5 text-blue-600" />
                  <span className="text-sm font-medium text-blue-900">Almacenamiento</span>
                </div>
                <span className="text-lg font-bold text-blue-900">{formatBytes(dbStats.mainDB.storageSize)}</span>
              </div>
            </div>

            <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-xl border border-green-200">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-green-600" />
                  <span className="text-sm font-medium text-green-900">Datos</span>
                </div>
                <span className="text-lg font-bold text-green-900">{formatBytes(dbStats.mainDB.dataSize)}</span>
              </div>
            </div>

            <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border border-purple-200">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Archive className="h-5 w-5 text-purple-600" />
                  <span className="text-sm font-medium text-purple-900">Índices</span>
                </div>
                <span className="text-lg font-bold text-purple-900">{formatBytes(dbStats.mainDB.indexSize)}</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                <div className="flex items-center gap-2 mb-1">
                  <Layers className="h-4 w-4 text-gray-500" />
                  <span className="text-xs text-gray-500">Colecciones</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">{dbStats.mainDB.collections}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                <div className="flex items-center gap-2 mb-1">
                  <FileText className="h-4 w-4 text-gray-500" />
                  <span className="text-xs text-gray-500">Documentos</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">{dbStats.mainDB.objects.toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Sales Database */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow duration-300">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl shadow-md">
              <TrendingUp className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Base de Datos de Ventas</h2>
              <p className="text-sm text-gray-500">Sales0 - Ventas y Gastos</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <HardDrive className="h-5 w-5 text-blue-600" />
                  <span className="text-sm font-medium text-blue-900">Almacenamiento</span>
                </div>
                <span className="text-lg font-bold text-blue-900">{formatBytes(dbStats.salesDB.storageSize)}</span>
              </div>
            </div>

            <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-xl border border-green-200">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-green-600" />
                  <span className="text-sm font-medium text-green-900">Datos</span>
                </div>
                <span className="text-lg font-bold text-green-900">{formatBytes(dbStats.salesDB.dataSize)}</span>
              </div>
            </div>

            <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border border-purple-200">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Archive className="h-5 w-5 text-purple-600" />
                  <span className="text-sm font-medium text-purple-900">Índices</span>
                </div>
                <span className="text-lg font-bold text-purple-900">{formatBytes(dbStats.salesDB.indexSize)}</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                <div className="flex items-center gap-2 mb-1">
                  <Layers className="h-4 w-4 text-gray-500" />
                  <span className="text-xs text-gray-500">Colecciones</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">{dbStats.salesDB.collections}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                <div className="flex items-center gap-2 mb-1">
                  <FileText className="h-4 w-4 text-gray-500" />
                  <span className="text-xs text-gray-500">Documentos</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">{dbStats.salesDB.objects.toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DatabaseStats;